import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronUp, BookOpen } from "lucide-react";

// Full-screen vertical scroll comic reader.
// Page images live at /comics/{issue-id}/page-{n}.jpg — swap those files to change art.
export const ComicReader = ({ comic, onClose }) => {
  const scrollRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [progress, setProgress] = useState(0);
  const total = comic.pages.length;

  // lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ESC to close
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  // Track scroll progress + current page via IntersectionObserver
  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    const onScroll = () => {
      const p = root.scrollTop / Math.max(1, root.scrollHeight - root.clientHeight);
      setProgress(Math.min(1, Math.max(0, p)));
    };
    root.addEventListener("scroll", onScroll, { passive: true });
    return () => root.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    const pages = root.querySelectorAll("[data-page]");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.55) {
            const n = Number(entry.target.getAttribute("data-page"));
            if (n) setCurrentPage(n);
          }
        });
      },
      { root, threshold: [0.55] }
    );
    pages.forEach((p) => io.observe(p));
    return () => io.disconnect();
  }, [comic.id]);

  const scrollTop = () => {
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[110] bg-[#050806]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        data-testid="comic-reader"
      >
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/95 via-black/70 to-transparent">
          <div className="max-w-4xl mx-auto flex items-center gap-3 px-4 sm:px-6 py-3">
            <button
              onClick={onClose}
              className="w-10 h-10 grid place-items-center rounded-full bg-white/10 hover:bg-emerald-500 hover:text-emerald-950 text-white border border-white/15 backdrop-blur transition-colors"
              aria-label="close reader"
              data-testid="reader-close"
            >
              <X size={20} />
            </button>
            <div className="flex-1 min-w-0">
              <p className="font-accent text-xs sm:text-sm tracking-[0.3em] text-emerald-400 truncate">
                <BookOpen size={11} className="inline mr-1" /> READER · ISSUE #{comic.issue}
              </p>
              <p className="font-display text-lg sm:text-xl text-white truncate">{comic.title}</p>
            </div>
            <div className="text-right">
              <p className="font-accent text-xs tracking-[0.2em] text-gray-400">PAGE</p>
              <p className="font-display text-2xl text-white leading-none" data-testid="reader-page-counter">
                <span className="text-emerald-400">{currentPage}</span>
                <span className="text-gray-500 text-lg"> / {total}</span>
              </p>
            </div>
          </div>
          {/* progress bar */}
          <div className="h-0.5 bg-white/10">
            <div
              className="h-full bg-emerald-400 transition-[width] duration-200"
              style={{ width: `${progress * 100}%` }}
              data-testid="reader-progress"
            />
          </div>
        </div>

        {/* Vertical scroll pages */}
        <div
          ref={scrollRef}
          className="absolute inset-0 overflow-y-auto overflow-x-hidden overscroll-contain scroll-smooth"
          style={{ paddingTop: 76 }}
          data-testid="reader-scroll"
        >
          <div className="max-w-3xl mx-auto px-2 sm:px-6 pb-24 pt-2">
            {comic.pages.map((src, i) => (
              <div
                key={i}
                data-page={i + 1}
                className="relative mb-3 sm:mb-5 rounded-lg overflow-hidden border border-white/10 bg-black shadow-2xl"
              >
                <img
                  src={src}
                  alt={`${comic.title} — Page ${i + 1}`}
                  loading={i < 2 ? "eager" : "lazy"}
                  className="w-full h-auto block select-none"
                />
                <div className="absolute top-2 left-2 bg-black/70 backdrop-blur text-white text-[10px] font-accent tracking-widest px-2 py-0.5 rounded">
                  {String(i + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
                </div>
              </div>
            ))}
            {/* End card */}
            <div className="rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-950/40 to-black text-center p-8 mt-4" data-testid="reader-end">
              <p className="font-accent text-lg tracking-[0.3em] text-emerald-400">THE END · ISSUE #{comic.issue}</p>
              <p className="font-display text-3xl text-white mt-2">{comic.title}</p>
              <p className="text-gray-400 mt-2">More issues incoming. Follow NAIJAMAN and get the next drop first.</p>
              <div className="flex flex-wrap gap-3 justify-center mt-6">
                <button
                  onClick={scrollTop}
                  className="btn-skew bg-white/10 hover:bg-white/20 text-white font-accent text-lg tracking-wide px-5 py-2"
                  data-testid="reader-back-to-top"
                >
                  <span className="flex items-center gap-2"><ChevronUp size={16} /> Back to Top</span>
                </button>
                <button
                  onClick={onClose}
                  className="btn-skew bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-accent text-lg tracking-wide px-5 py-2 glow-green"
                  data-testid="reader-close-end"
                >
                  <span>Close Reader</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
